import type { Task } from "~/types/task";
import type { Section } from "~/types/section";

/**
 * API responses may use snake_case or camelCase inconsistently.
 * This normalizer ensures consistent camelCase throughout the app.
 */

interface RawTaskFromAPI {
  id: number;
  title: string;
  text?: Record<string, string>;
  type: string;
  start_date?: string;
  end_date?: string;
  status: string;
  priority: string;
  author?: {
    id: number;
    firstname: string;
    surname: string;
    avatar?: string;
  };
  assigned_to?: number;
  assigned_user?: {
    id: number;
    firstname: string;
    surname: string;
    avatar?: string;
  };
  linkedToId?: number;
  linked_to_id?: number;
  linkedToType?: string;
  linked_to_type?: string;
  childId?: number;
  child_id?: number;
  residentId?: number;
  resident_id?: number;
  resident?: {
    id: number;
    forename: string;
    surname: string;
    gender?: string;
  };
  child?: {
    id: number;
    full_name: string;
    gender?: string;
  };
  sectionId?: number;
  section_id?: number;
  order?: number;
  created_at?: string;
  updated_at?: string;
  section?: Section;
  family_group?: { id: number; name: string };
  family_group_id?: number;
  recurrence_pattern?: string;
  recurrence_start_at?: string;
  recurrence_end_at?: string;
  parent_task_id?: number;
  archived?: boolean;
  archived_at?: string | null;
  completed_at?: string;
  review_submitted_at?: string;
  review_submitted_by?: number;
  review_submitted_by_user?: {
    id: number;
    firstname: string;
    surname: string;
    avatar?: string;
  };
  approved_at?: string;
  approved_by?: number;
  approved_by_user?: {
    id: number;
    firstname: string;
    surname: string;
    avatar?: string;
  };
  rejected_at?: string;
  rejected_by?: number;
  rejected_by_user?: {
    id: number;
    firstname: string;
    surname: string;
    avatar?: string;
  };
  changes_requested_at?: string;
  changes_requested_by?: number;
  changes_requested_by_user?: {
    id: number;
    firstname: string;
    surname: string;
    avatar?: string;
  };
  rejection_reason?: string;
}

interface RawSectionFromAPI {
  id: number;
  name: string;
  description?: string;
  access_level?: Section["access_level"];
  is_system?: boolean;
  family_group?: { id: number; name: string; icon?: string; color?: string };
  family_group_id?: number;
  created_at?: string;
  updated_at?: string;
  tasks?: RawTaskFromAPI[];
  task_default_type?: string;
}

export function normalizeTask(raw: RawTaskFromAPI): Task {
  return {
    id: raw.id,
    title: raw.title,
    text: raw.text ?? null,
    type: raw.type as Task["type"],
    start_date: raw.start_date ?? null,
    end_date: raw.end_date ?? null,
    status: raw.status as Task["status"],
    priority: raw.priority as Task["priority"],
    author: raw.author ?? null,
    assigned_to: raw.assigned_to ?? raw.assigned_to ?? null,
    assigned_user: raw.assigned_user ?? null,
    linked_to_id: raw.linkedToId ?? raw.linked_to_id ?? null,
    linked_to_type: (raw.linkedToType ?? raw.linked_to_type ?? null) as Task["linked_to_type"],
    child_id: raw.childId ?? raw.child_id ?? null,
    resident_id: raw.residentId ?? raw.resident_id ?? null,
    resident: raw.resident ?? null,
    child: raw.child ?? null,
    section_id: raw.sectionId ?? raw.section_id ?? null,
    order: raw.order ?? 0,
    created_at: raw.created_at ?? null,
    updated_at: raw.updated_at ?? null,
    section: raw.section ?? null,
    family_group: raw.family_group ?? null,
    family_group_id: raw.family_group_id ?? null,

    recurrence_pattern: raw.recurrence_pattern ?? null,
    recurrence_start_at: raw.recurrence_start_at ?? null,
    recurrence_end_at: raw.recurrence_end_at ?? null,
    parent_task_id: raw.parent_task_id ?? null,

    archived: raw.archived ?? false,
    archived_at: raw.archived_at ?? null,

    completed_at: raw.completed_at ?? null,
    review_submitted_at: raw.review_submitted_at ?? null,
    review_submitted_by: raw.review_submitted_by ?? null,
    review_submitted_by_user: raw.review_submitted_by_user ?? null,
    approved_at: raw.approved_at ?? null,
    approved_by: raw.approved_by ?? null,
    approved_by_user: raw.approved_by_user ?? null,
    rejected_at: raw.rejected_at ?? null,
    rejected_by: raw.rejected_by ?? null,
    rejected_by_user: raw.rejected_by_user ?? null,
    changes_requested_at: raw.changes_requested_at ?? null,
    changes_requested_by: raw.changes_requested_by ?? null,
    changes_requested_by_user: raw.changes_requested_by_user ?? null,
    rejection_reason: raw.rejection_reason ?? null,
  };
}

export function normalizeTasks(rawTasks: RawTaskFromAPI[]): Task[] {
  return rawTasks.map(normalizeTask);
}

export function normalizeSection(raw: RawSectionFromAPI): Section {
  return {
    id: raw.id,
    name: raw.name,
    description: raw.description ?? null,
    access_level: (raw.access_level ?? "member") as Section["access_level"],
    is_system: raw.is_system ?? false,
    family_group: raw.family_group
      ? {
          id: raw.family_group.id,
          name: raw.family_group.name,
          icon:
            "icon" in raw.family_group && typeof raw.family_group.icon === "string"
              ? raw.family_group.icon
              : "",
          color: raw.family_group.color ?? "",
        }
      : null,
    created_at: raw.created_at ?? null,
    updated_at: raw.updated_at ?? null,
    family_group_id: raw.family_group_id ?? null,
    task_default_type: (raw.task_default_type ?? "task") as Task["type"],
  };
}

export function normalizeSections(rawSections: RawSectionFromAPI[]): Section[] {
  return rawSections.map(normalizeSection);
}
